import { Heart, MessageCircle, Repeat, Bookmark, Trash2, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { postService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { UserAvatar } from '@/components/ui/Avatar';
import { Modal } from '@/components/ui/Modal';
import { formatRelativeDate, cn } from '@/lib/utils';
import { ROUTES } from '@/constants';

interface PostCardProps {
  post: any;
  onLikeChange?: (postId: string, liked: boolean) => void;
}

export default function PostCard({ post, onLikeChange }: PostCardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isLiked, setIsLiked] = useState<boolean>(
    () => !!post.likes?.some((l: any) => l.user_id === user?.id),
  );
  const [likeCount, setLikeCount] = useState<number>(post.likes?.length || 0);
  const [isReposted, setIsReposted] = useState<boolean>(
    () => !!post.reposts?.some((r: any) => r.user_id === user?.id),
  );
  const [repostCount, setRepostCount] = useState<number>(post.reposts?.length || 0);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(
    () => !!post.bookmarks?.some((b: any) => b.user_id === user?.id),
  );
  const [error, setError] = useState<string | null>(null);

  const closeError = () => setError(null);

  const showError = (message: string) => {
    setError(message);
    toast('Failed', message, 'destructive');
  };

  const handleLike = async () => {
    if (!user) { navigate(ROUTES.LOGIN); return; }

    const prevLiked = isLiked;
    const prevCount = likeCount;
    setIsLiked(!prevLiked);
    setLikeCount((c: number) => (prevLiked ? Math.max(0, c - 1) : c + 1));
    onLikeChange?.(post.id, !prevLiked);

    try {
      if (prevLiked) {
        await postService.unlikePost(post.id);
      } else {
        await postService.likePost(post.id);
      }
    } catch (err: any) {
      setIsLiked(prevLiked);
      setLikeCount(prevCount);
      onLikeChange?.(post.id, prevLiked);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleRepost = async () => {
    if (!user) { navigate(ROUTES.LOGIN); return; }

    const prevReposted = isReposted;
    const prevCount = repostCount;
    setIsReposted(!prevReposted);
    setRepostCount((c: number) => (prevReposted ? Math.max(0, c - 1) : c + 1));

    try {
      if (prevReposted) {
        await postService.unrepostPost(post.id);
      } else {
        await postService.repostPost(post.id);
      }
    } catch (err: any) {
      setIsReposted(prevReposted);
      setRepostCount(prevCount);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleBookmark = async () => {
    if (!user) { navigate(ROUTES.LOGIN); return; }

    const prevBookmarked = isBookmarked;
    setIsBookmarked(!prevBookmarked);

    try {
      if (prevBookmarked) {
        await postService.unbookmarkPost(post.id);
      } else {
        await postService.bookmarkPost(post.id);
      }
    } catch (err: any) {
      setIsBookmarked(prevBookmarked);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    const { error } = await postService.deletePost(post.id);
    if (error) showError(error.message);
    else {
      toast('Post deleted');
      navigate(ROUTES.HOME);
    }
  };

  const isOwnPost = user && post.user_id === user.id;
  const profileHref = ROUTES.PROFILE.replace(':username', post.profiles?.username || 'me');

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        // Mobile: bottom border only, padding inside, no corner radius, no background block, full bleed.
        'border-b border-neutral-200 dark:border-neutral-800 p-3',
        // Desktop: full post card with padding, rounded corners, full border, no background block.
        'sm:post-card sm:p-4 sm:bg-transparent sm:dark:bg-transparent',
      )}
    >
      {post.original_post_id && post.is_repost && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
          <Repeat size={12} /> Reposted
        </div>
      )}
      <div className="flex gap-3">
        <Link to={profileHref} className="shrink-0">
          <UserAvatar src={post.profiles?.avatar_url} size="md" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Link to={profileHref} className="max-w-full truncate font-semibold hover:underline">
              {post.profiles?.display_name || post.profiles?.username}
            </Link>
            <span className="max-w-full truncate text-sm text-neutral-500 dark:text-neutral-400">
              @{post.profiles?.username}
            </span>
            <span className="text-xs text-neutral-400 dark:text-neutral-500">
              · {formatRelativeDate(post.created_at)}
            </span>
          </div>

          <div className="mb-3 whitespace-pre-wrap break-words text-sm leading-relaxed">
            {post.content}
          </div>

          {post.post_media && post.post_media.length > 0 && (
            <div className="mb-3 grid grid-cols-2 gap-2">
              {post.post_media.map((m: any, i: number) => (
                <img
                  key={i}
                  src={m.url}
                  alt="Post media"
                  loading="lazy"
                  className="aspect-square w-full rounded-xl object-cover"
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1 text-sm sm:gap-2">
            <button
              onClick={handleLike}
              className={cn(
                'flex items-center gap-1 rounded-lg px-2 py-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                isLiked ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-600 dark:text-neutral-300',
              )}
              aria-label={isLiked ? 'Unlike' : 'Like'}
            >
              <Heart size={18} className={cn(isLiked && 'fill-current')} />
              <span>{likeCount}</span>
            </button>

            <button
              onClick={() => navigate(ROUTES.POST.replace(':id', post.id))}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Comments"
            >
              <MessageCircle size={18} />
              <span>{post.comments_count || 0}</span>
            </button>

            <button
              onClick={handleRepost}
              className={cn(
                'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                isReposted ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-600 dark:text-neutral-300',
              )}
              aria-label={isReposted ? 'Undo repost' : 'Repost'}
            >
              <Repeat size={18} />
              <span>{repostCount}</span>
            </button>

            <button
              onClick={handleBookmark}
              className={cn(
                'flex items-center rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                isBookmarked ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-600 dark:text-neutral-300',
              )}
              aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
            >
              <Bookmark size={18} className={cn(isBookmarked && 'fill-current')} />
            </button>

            {isOwnPost && (
              <button
                onClick={handleDelete}
                className="ml-auto flex items-center rounded-lg px-2 py-1.5 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                aria-label="Delete post"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <Modal isOpen={!!error} onClose={closeError}>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Something went wrong
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">{error}</p>
          <button
            onClick={closeError}
            className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <X size={16} className="mr-2" />
            Dismiss
          </button>
        </Modal>
      )}
    </motion.div>
  );
}
